import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class KeycloakService {
  private keycloak: Keycloak;

  constructor(
    private readonly cookieService: CookieService
  ) {}

  async init(): Promise<void> {
    this.keycloak = new Keycloak({
      url: environment.keycloakUrl,
      realm: environment.keycloakRealm,
      clientId: environment.keycloakClientId
    });
    await this.keycloak.init({ onLoad: 'login-required', pkceMethod: 'S256' });
  }

  getToken(): string {
    const token = this.keycloak?.token || '';
    if (!token) return '';

    if (this.isTokenExpired(token)) {
      this.logout(false);
      return '';
    }

    if (this.isTokenAboutToExpire(token)) {
      this.updateToken().catch(() => this.logout(false));
    }

    return token;
  }

  /**
   * Checks if the given JWT token is expired.
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) return false;
      return Date.now() >= exp * 1000;
    } catch {
      return false;
    }
  }

  /**
   * Checks if the given JWT token is about to expire (less than 2 minutes left).
   */
  isTokenAboutToExpire(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) return false;
      const minutesLeft = (exp * 1000 - Date.now()) / 60000;
      return minutesLeft < 2;
    } catch {
      return false;
    }
  }

  /**
   * Silently refresh the token if it will expire in less than minValidity seconds. (default 30 minutes)
   * Resolves true if refreshed, false if still valid, rejects if refresh fails.
   */
  async updateToken(minValidity: number = 1800): Promise<boolean> {
    if (!this.keycloak) {
      throw new Error('Keycloak not initialized');
    }
    return await this.keycloak.updateToken(minValidity);
  }

  logout(permissionError : boolean): void {
    // Remove Keycloak tokens from localStorage/sessionStorage
    try {
/*       localStorage.clear();
      sessionStorage.clear();
      this.cookieService.deleteAll('/');
      this.cookieService.deleteAll(); */
    } catch (e) {
      // Ignore errors
    }
    const redirectUri = permissionError ? window.location.origin + '/permissionError.html' : window.location.origin;
    this.keycloak?.logout({ redirectUri: redirectUri });
  }
}
